import { HistoryOutlined } from '@ant-design/icons';
import { useHead } from '@unhead/react';
import { Flex, message } from 'antd';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import NewReportForm from 'business/shift-report/report/components/NewReportForm';
import ReportCard from 'business/shift-report/report/components/ReportCard';
import MaintenanceAlert from 'business/user/components/maintenanceAlert';
import { ROLES } from 'business/user/services/config';
import Routes from 'config/routes';
import { Module_Enum, useGetPreviousReportQuery } from 'generated/graphql';
import Button from 'ui/button';
import { PreviousPageButton } from 'ui/button/previous-page-button';
import Loader from 'ui/loader';

import styles from './index.module.scss';

function ReportCreatePage() {
  const { t } = useTranslation();

  useHead({ title: t('user.menu.sections.reporting.title') });

  const { currentConstructionSite } = useAppContext();
  const {
    modules: {
      SHIFT_REPORT: { hasManagerReadPermission, role },
    },
  } = usePermissions();

  const headers = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const { loading, error, data, refetch } = useGetPreviousReportQuery({
    variables: {
      constructionSiteId: currentConstructionSite?.id,
    },
    context: {
      headers,
    },
    fetchPolicy: 'network-only',
    skip: !currentConstructionSite?.id,
  });

  useEffect(
    function alertUser() {
      if (error) {
        message.error(error.message);
      }
    },
    [error],
  );

  const lastReport =
    data?.shiftReport && data.shiftReport.length > 0
      ? data.shiftReport[0]
      : undefined;

  return (
    <Flex
      flex={1}
      vertical
      justify="space-around"
      gap={20}
      className={classNames(
        styles.reportCreatePage,
        'main-bg-image',
        'page-appear',
      )}
    >
      <MaintenanceAlert />
      {loading && <Loader />}
      {!loading && (
        <>
          <NewReportForm previousReport={lastReport} />
          {lastReport && (
            <ReportCard
              report={lastReport}
              refetchList={refetch}
              routeRedirection={{
                redirect:
                  role === ROLES.USER
                    ? Routes.ReportUserHome
                    : Routes.ReportCreate,
              }}
            />
          )}

          <Link to={{ pathname: Routes.ReportList }} className="button-center">
            <Button type="primary" icon={<HistoryOutlined />}>
              {t('pages.report.new.openReportHistory')}
            </Button>
          </Link>
          {hasManagerReadPermission ? (
            <PreviousPageButton
              to={Routes.ReportDashboardVisualize}
              label="common.to_main_page"
            />
          ) : null}
        </>
      )}
    </Flex>
  );
}

export default ReportCreatePage;
